import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import {
  LayoutSize,
  useLayoutSizeValue,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { ListOption } from '@/components/list-item';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { PageContainer } from '@/components/page-container';
import {
  PromotionListOption,
  usePromotionListOptions,
} from '@/hooks/use-promotion-list-options';
import { RecyclerList } from '@/components/recycler-list';
import { SCREEN_PADDING } from '@/styles';
import {
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
  ViewToken,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTrackPromotionImpressions } from '@/hooks/use-track-promotion-impressions';
import { VolumePromotionListItem } from './volume-promotion-list-item';
import { VolumePromotionListItemOrderableItem } from './volume-promotion-list-item-orderable-item';

const LAYOUT_SIZE_TO_COLUMNS_MAP: { [size in LayoutSize]: number } = {
  [LayoutSize.SMALL]: 1,
  [LayoutSize.SMALL_TABLET]: 2,
  [LayoutSize.MEDIUM]: 2,
  [LayoutSize.LARGE]: 3,
};

const PROMOTIONS_LIST_SCREEN_NAME = 'promotions_list_screen';

type VolumePromotionsListOption = ListOption<{
  promotions: PromotionListOption[];
}>;

function* promotionGenerator(
  viewableItems: ViewToken<VolumePromotionsListOption>[],
) {
  for (const viewableItem of viewableItems) {
    for (const promotion of viewableItem.item.metadata!.promotions) {
      yield promotion.metadata!.promotion.uuid;
    }
  }
}

interface Props {
  headerBackButtonLabel: string;
  onNavigateToDetails: (promotionUUID: string) => void;
}

export const VolumePromotionsList: React.FC<Props> = ({
  headerBackButtonLabel,
  onNavigateToDetails,
}) => {
  const promotions = usePromotionListOptions();
  const { bottom } = useSafeAreaInsets();
  const { onViewableItemsChanged } = useTrackPromotionImpressions({
    promotionGenerator,
    trackingLocation: PROMOTIONS_LIST_SCREEN_NAME,
  });

  const columnCount = useLayoutSizeValue(LAYOUT_SIZE_TO_COLUMNS_MAP);
  const listContentContainerStyle = useSmallLayoutSizeValue<
    StyleProp<ViewStyle>
  >(styles.listContentContainer, [
    styles.listContentContainer,
    styles.listContentContainerLarge,
    { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
  ]);

  const baseNavHeaderProps = useSmallLayoutSizeValue<
    Partial<NavigationHeaderProps>
  >(
    {
      showBackButton: true,
    },
    {
      backButtonLabel: headerBackButtonLabel,
      showBackButton: false,
    },
  );

  const navigationHeaderProps = useMemo(() => {
    return {
      ...baseNavHeaderProps,
      responsive: true,
      title: i18n.t('promotions'),
    };
  }, [baseNavHeaderProps]);

  const sections = useMemo(() => {
    // Group promotions into rows based on the number of columns so items
    // within the same row can be rendered with the same height
    const groupedPromotions: VolumePromotionsListOption[] = [];
    for (let i = 0; i < promotions.length; i += columnCount) {
      groupedPromotions.push({
        metadata: {
          promotions: promotions.slice(i, i + columnCount),
        },
        title: `promotions-${i}`,
        value: `promotions-${i}`,
      });
    }

    return [{ data: groupedPromotions, key: null }];
  }, [promotions, columnCount]);

  return (
    <PageContainer navigationHeaderProps={navigationHeaderProps}>
      <RecyclerList<VolumePromotionsListOption>
        contentContainerStyle={listContentContainerStyle}
        estimatedItemSize={220}
        ItemSeparatorComponent={null}
        onViewableItemsChanged={onViewableItemsChanged}
        renderItem={({ item: listItem }) => {
          return (
            <View style={styles.listItemRow}>
              {listItem.metadata!.promotions.map((item) => {
                const { promotion } = item.metadata!;
                const PromotionListItemComponent =
                  promotion.promotedVariantPacks.length === 1
                    ? VolumePromotionListItemOrderableItem
                    : VolumePromotionListItem;

                return (
                  <View key={promotion.uuid} style={{ flex: 1 / columnCount }}>
                    <View style={styles.listItemContainer}>
                      <PromotionListItemComponent
                        onNavigateToDetails={onNavigateToDetails}
                        promotion={promotion}
                        source="promotions_list"
                        style={styles.listItem}
                      />
                    </View>
                  </View>
                );
              })}
            </View>
          );
        }}
        sections={sections}
      />
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  listContentContainer: {
    paddingTop: 12,
  },
  listContentContainerLarge: {
    paddingHorizontal: 16,
  },
  listItem: {
    flex: 1,
  },
  listItemContainer: {
    flex: 1,
    marginBottom: 12,
    marginHorizontal: 16,
  },
  listItemRow: {
    flexDirection: 'row',
  },
});
